import React, { useState, useRef, useEffect } from 'react';
import DropDownIcon from '../Icons/DropDownIcon';
import SearchIcon from '../Icons/SearchIcon';
import styles from './CustomDropdown.module.css';

const CustomDropdown = ({ 
    options, 
    value, 
    onChange, 
    placeholder = "Select option",
    disabled = false,
    searchPlaceholder = "Search...",
    disableSearch = false,
    autoFocusSearch = false,
    renderRight ,// optional: function to render right-aligned content for each option
    triggerPaddingX, // optional: number: padding-left/right for trigger
    hideUnderline = false, // optional: remove bottom border on trigger
    hideIcon = false, // optional: hide dropdown chevron
    onOpen, // optional: callback when dropdown opens
    openSignal, // optional: changing this value opens the dropdown
    compactTrigger = false, // optional: place chevron next to selected text instead of edge
    triggerColor, // optional: text color of selected value
    /** When true, menu spans the width of the nearest positioned ancestor (parent should be `position: relative`) */
    alignMenuToRow = false,
    /** Optional extra class on the menu panel (use for layout overrides, e.g. full-width checkout rows) */
    menuClassName = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const previousOpenSignalRef = useRef(openSignal);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Notify parent on open so it can scroll the container
    useEffect(() => {
        if (isOpen && typeof onOpen === 'function') {
            onOpen();
        }
    }, [isOpen, onOpen]);

    useEffect(() => {
        if (isOpen && !disableSearch && autoFocusSearch) {
            const focusTimeout = setTimeout(() => {
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            }, 0);

            return () => clearTimeout(focusTimeout);
        }
    }, [isOpen, disableSearch, autoFocusSearch]);

    useEffect(() => {
        if (disabled) {
            previousOpenSignalRef.current = openSignal;
            return;
        }

        if (previousOpenSignalRef.current !== openSignal) {
            setIsOpen(true);
        }

        previousOpenSignalRef.current = openSignal;
    }, [openSignal, disabled]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleOptionClick = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm(''); // Clear search when option is selected
    };

    const selectedOption = options.find(option => option.value === value);

    // Check if this is a date dropdown (has "All Time" option)
    const isDateDropdown = options.some(option => option.value === 'All Time');

    // Filter options based on search term - only if search is enabled
    const filteredOptions = disableSearch ? options : options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div
            className={`${styles.dropdownContainer} ${isDateDropdown ? styles.dateDropdown : ''} ${
                alignMenuToRow ? styles.dropdownContainerBreakout : ''
            }`}
            ref={dropdownRef}
        >
            <div 
                className={`${styles.dropdownTrigger} ${disabled ? styles.disabled : ''} ${compactTrigger ? styles.compact : ''}`}
                onClick={handleToggle}
                style={{
                    ...(typeof triggerPaddingX === 'number' ? { paddingLeft: triggerPaddingX, paddingRight: triggerPaddingX } : {}),
                    ...(hideUnderline ? { borderBottom: 'none' } : {}),
                }}
            >
                <span
                    className={styles.selectedValue}
                    style={{
                        ...(compactTrigger ? { flex: '0 0 auto' } : {}),
                        ...(triggerColor ? { color: triggerColor } : {}),
                    }}
                >
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                {!hideIcon && (
                    <DropDownIcon 
                        size={12} 
                        color={triggerColor || '#6b7280'} 
                        className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ''}`}
                    />
                )}
            </div>
            
            {isOpen && (
                <div
                    className={`${styles.dropdownMenu} ${alignMenuToRow ? styles.dropdownMenuFullRow : ''} ${menuClassName || ''}`}
                    style={
                        !alignMenuToRow && typeof triggerPaddingX === 'number'
                            ? { left: triggerPaddingX, right: triggerPaddingX }
                            : undefined
                    }
                >
                    {/* Search Bar - only show if search is not disabled */}
                    {!disableSearch && (
                        <div className={styles.searchContainer}>
                            <SearchIcon />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus={autoFocusSearch}
                            />
                        </div>
                    )}

                    {/* Options List */}
                    <div className={styles.optionsList}>
                        {filteredOptions.map((option, index) => (
                            <div
                                key={option.value}
                                className={`${styles.dropdownOption} ${
                                    option.value === value ? styles.selected : ''
                                } ${option.isHeader ? styles.headerOption : ''} ${
                                    option.disabled ? styles.disabledOption : ''
                                }`}
                                onClick={() => !option.isHeader && !option.disabled && handleOptionClick(option.value)}
                                style={{ 
                                    cursor: option.isHeader ? 'default' : (option.disabled ? 'not-allowed' : 'pointer')
                                }}
                                data-value={option.value}
                            >
                                <span>{option.label}</span>
                                {!option.isHeader && renderRight && (
                                    <span style={{ marginLeft: 'auto' }}>{renderRight(option)}</span>
                                )}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className={styles.noResults}>
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomDropdown;
